/** Buttons, cards, chips, banners, stats, empty states and the small parts every screen uses (docs/52 §4.5). */
import type { ComponentChildren, JSX } from 'preact';
import { Icon, type IconName } from '../icons.tsx';

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger' | 'danger-solid';

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  /** Shows a progress bar inside the button and keeps its width (docs/52 §4.5). */
  busy?: boolean;
  /** Disabled buttons stay focusable and say why through `describedBy`. */
  disabled?: boolean;
  icon?: IconName;
  iconEnd?: IconName;
  href?: string;
  /** Opens outside the app (receipts, docs/52 D9). */
  external?: boolean;
  type?: 'button' | 'submit';
  onClick?: (e: MouseEvent) => void;
  children: ComponentChildren;
  describedBy?: string;
  form?: string;
  class?: string;
  id?: string;
  'aria-label'?: string;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
}

export function Button(p: ButtonProps): JSX.Element {
  const common = {
    class: `btn${p.class ? ` ${p.class}` : ''}`,
    'data-variant': p.variant ?? 'secondary',
    'data-size': p.size ?? 'md',
    'data-block': p.block ? '' : undefined,
    'data-busy': p.busy ? '' : undefined,
    'aria-busy': p.busy ? true : undefined,
    'aria-disabled': p.disabled || p.busy ? true : undefined,
    'aria-describedby': p.describedBy,
    'aria-label': p['aria-label'],
    id: p.id,
  } as const;
  const inner = (
    <>
      {p.icon && <Icon name={p.icon} />}
      <span>{p.children}</span>
      {p.iconEnd && <Icon name={p.iconEnd} />}
    </>
  );
  if (p.href) {
    return (
      <a
        {...common}
        href={p.href}
        target={p.external ? '_blank' : undefined}
        rel={p.external ? 'noopener noreferrer' : undefined}
        onClick={(e) => {
          if (p.disabled) e.preventDefault();
          else p.onClick?.(e as unknown as MouseEvent);
        }}
      >
        {inner}
      </a>
    );
  }
  return (
    <button
      {...common}
      type={p.type ?? 'button'}
      form={p.form}
      aria-expanded={p['aria-expanded']}
      aria-controls={p['aria-controls']}
      onClick={(e) => {
        if (p.disabled || p.busy) {
          e.preventDefault();
          return;
        }
        p.onClick?.(e as unknown as MouseEvent);
      }}
    >
      {inner}
    </button>
  );
}

export function Card(p: { children: ComponentChildren; tone?: 'frame'; pad?: 'none'; class?: string; as?: 'section' | 'div' | 'article'; labelledBy?: string }): JSX.Element {
  const Tag = p.as ?? 'section';
  return (
    <Tag class={`card${p.class ? ` ${p.class}` : ''}`} data-tone={p.tone} data-pad={p.pad} aria-labelledby={p.labelledBy}>
      {p.children}
    </Tag>
  );
}

export function CardHead(p: { title: ComponentChildren; id?: string; level?: 2 | 3; info?: JSX.Element | null; aside?: ComponentChildren }): JSX.Element {
  const H = p.level === 3 ? 'h3' : 'h2';
  return (
    <div class="card-head">
      <H id={p.id} class="t-heading">
        {p.title}
      </H>
      {p.aside}
      {p.info}
    </div>
  );
}

export type Tone = 'new' | 'ok' | 'warn' | 'danger' | 'muted' | 'outline' | 'neutral';

const TONE_ICON: Record<Tone, IconName | null> = { new: null, ok: 'check', warn: 'clock', danger: 'alert', muted: null, outline: null, neutral: null };

/** Status chip: text plus a shape, never colour alone (docs/52 §4.5). */
export function Chip(p: { tone?: Tone; icon?: IconName | null; children: ComponentChildren }): JSX.Element {
  const tone = p.tone ?? 'neutral';
  const icon = p.icon === undefined ? TONE_ICON[tone] : p.icon;
  return (
    <span class="chip" data-tone={tone === 'neutral' ? undefined : tone}>
      {tone === 'new' ? <span class="chip-dot" aria-hidden="true" /> : icon ? <Icon name={icon} size={14} /> : null}
      {p.children}
    </span>
  );
}

export function Banner(p: { tone?: 'info' | 'warn' | 'danger' | 'ok' | 'frame'; icon?: IconName; title?: ComponentChildren; children?: ComponentChildren; action?: JSX.Element | null; role?: 'status' | 'alert' }): JSX.Element {
  const icon = p.icon ?? (p.tone === 'danger' ? 'alert' : p.tone === 'warn' ? 'clock' : p.tone === 'ok' ? 'check' : 'info');
  return (
    <div class="banner" data-tone={p.tone === 'info' ? undefined : p.tone} role={p.role}>
      <Icon name={icon} />
      <div class="banner-body">
        {p.title && <p><strong>{p.title}</strong></p>}
        {p.children && <div>{p.children}</div>}
        {p.action}
      </div>
    </div>
  );
}

export function InfoButton(p: { label: string; onClick: () => void; controls?: string }): JSX.Element {
  return (
    <button type="button" class="info-btn" aria-label={p.label} aria-haspopup="dialog" aria-controls={p.controls} onClick={p.onClick}>
      <span aria-hidden="true">i</span>
    </button>
  );
}

export function Stat(p: { label: string; value: ComponentChildren; compare?: ComponentChildren; trend?: 'up' | 'down' | 'flat' | null; info?: JSX.Element | null; stamp?: ComponentChildren }): JSX.Element {
  return (
    <div class="stat">
      <div class="stat-top">
        <span class="stat-label">{p.label}</span>
        {p.info}
      </div>
      <span class="t-figure">{p.value}</span>
      {p.compare && (
        <span class="stat-compare">
          {p.trend === 'up' && <Icon name="arrow-up" size={16} />}
          {p.trend === 'down' && <Icon name="arrow-down" size={16} />}
          {p.compare}
        </span>
      )}
      {p.stamp && <span class="stat-compare">{p.stamp}</span>}
    </div>
  );
}

export function SourceBadge(p: { icon?: IconName; children: ComponentChildren }): JSX.Element {
  return (
    <span class="source">
      <Icon name={p.icon ?? 'check'} size={16} />
      {p.children}
    </span>
  );
}

export function EmptyState(p: { title: ComponentChildren; body?: ComponentChildren; action?: JSX.Element | null; headingLevel?: 2 | 3 }): JSX.Element {
  const H = p.headingLevel === 3 ? 'h3' : 'h2';
  return (
    <div class="empty">
      <H>{p.title}</H>
      {p.body && <p>{p.body}</p>}
      {p.action}
    </div>
  );
}

export function Skeleton(p: { kind: 'line' | 'row' | 'card'; width?: string; label?: string }): JSX.Element {
  return <span class="skel" data-h={p.kind} style={p.width ? `inline-size:${p.width}` : undefined} aria-hidden="true" />;
}

export function ProgressRing(p: { done: number; total: number; size?: number; tone?: 'frame'; label: ComponentChildren; srLabel: string }): JSX.Element {
  const size = p.size ?? 88;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = p.total > 0 ? Math.min(1, p.done / p.total) : 0;
  return (
    <div class="ring" data-tone={p.tone} role="img" aria-label={p.srLabel} style={`inline-size:${size}px;block-size:${size}px`}>
      <svg width={size} height={size} aria-hidden="true">
        <circle class="ring-track" cx={size / 2} cy={size / 2} r={r} fill="none" stroke-width={stroke} />
        <circle
          class="ring-fill"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke-width={stroke}
          stroke-linecap="round"
          stroke-dasharray={c}
          stroke-dashoffset={c * (1 - pct)}
        />
      </svg>
      <span class="ring-label" aria-hidden="true">{p.label}</span>
    </div>
  );
}

export function KeyValue(p: { rows: [ComponentChildren, ComponentChildren][]; stacked?: boolean }): JSX.Element {
  return (
    <dl class="kv" data-stack={p.stacked ? '' : undefined}>
      {p.rows.map(([k, v], i) => (
        <>
          <dt key={`k${i}`}>{k}</dt>
          <dd key={`v${i}`}>{v}</dd>
        </>
      ))}
    </dl>
  );
}
